/* Pieces more than one screen uses, on the console's design system. */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '../data/api';
import { dateOnly } from '../data/format';
import { ambulanceType, errorText, shiftPattern } from '../data/words';
import { Note, uiCss } from '../ui';

/* ------------------------------------------------------------------ data */

export interface Async<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/** The console's useAsync (apps/admin/src/data/admin.ts): re-reads when a dep
 *  or the nonce changes, drops a reply that arrives after unmount, and NEVER
 *  swallows an error — "nothing here" and "we could not ask" are different
 *  facts. `pollMs` re-reads while the tab is visible. */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[], pollMs?: number): Async<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const latest = useRef(fn);
  latest.current = fn;

  useEffect(() => {
    let live = true;
    setLoading(true);
    latest
      .current()
      .then((d) => {
        if (!live) return;
        setData(d);
        setError(null);
      })
      .catch((e: unknown) => {
        if (!live) return;
        setError(e instanceof ApiError ? e.message : 'Could not reach the server.');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  useEffect(() => {
    if (!pollMs) return;
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') setNonce((n) => n + 1);
    }, pollMs);
    return () => window.clearInterval(t);
  }, [pollMs]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload };
}

/** A clock that ticks every second, for countdowns. */
export function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  return now;
}

/** Runs an action and reports how it went, the way the console's screens do:
 *  one notice line above the content, green or red, never a browser alert. */
export function useNotice() {
  const [notice, setNotice] = useState<{ tone: 'ok' | 'danger' | 'amber'; text: string } | null>(null);
  const run = useCallback(async (fn: () => Promise<unknown>, okText: string) => {
    setNotice(null);
    try {
      await fn();
      setNotice({ tone: 'ok', text: okText });
      return true;
    } catch (e) {
      setNotice({ tone: 'danger', text: errorText(e) });
      return false;
    }
  }, []);
  const view = notice ? <Note tone={notice.tone}>{notice.text}</Note> : null;
  return { notice: view, setNotice, run };
}

/* ------------------------------------------------------------------ facts */

/** A grid of label/value pairs — the console's fact grid. */
export function Facts({ items, wide }: { items: [ReactNode, ReactNode][]; wide?: boolean }) {
  return (
    <div className={wide ? uiCss.factGridWide : uiCss.factGrid}>
      {items.map(([k, v], i) => (
        <div key={i}>
          <div className={uiCss.factK}>{k}</div>
          <div className={uiCss.factV}>{v}</div>
        </div>
      ))}
    </div>
  );
}

/** Words for the service detail keys the server sends (dispatch_views.go
 *  pricingDetail, binding_views.go itemDetail). An unknown key is tidied. */
const KEY_WORDS: Record<string, string> = {
  start_date: 'From',
  end_date: 'Until',
  shift_pattern: 'Shift',
  shifts_per_day: 'Shifts a day',
  nurses_required: 'People needed',
  gender_preference: 'Gender asked for',
  care_task_codes: 'Care tasks',
  ambulance_type: 'Ambulance type',
  oxygen_required: 'Oxygen',
  ventilator_required: 'Ventilator',
  patient_mobility: 'Patient can move',
  attendant_count: 'Attendants',
  estimated_distance_km: 'Distance (estimated)',
  drop_facility_name: 'Going to',
  equipment_type_code: 'Equipment',
  units_required: 'Units',
  rental_start_date: 'Rental from',
  rental_basis: 'Rental basis',
  care_level: 'Care level',
  setup_date: 'Set up on',
  oxygen_support: 'Oxygen support',
  cardiac_monitoring: 'Heart monitoring',
  suction_required: 'Suction',
  consult_date: 'Consultation on',
  consult_time: 'Time',
  consult_mode: 'How',
  specialty: 'Specialty',
  visit_count: 'Visits',
  pickup_address_text: 'Pick up from',
  language_preference_code: 'Language asked for',
  rental_end_date: 'Rental until',
};

function tidyKey(k: string): string {
  const s = k.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function show(k: string, v: unknown): ReactNode {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.length ? v.map((x) => tidyKey(String(x)).toLowerCase()).join(', ') : 'None';
  if (k === 'shift_pattern') return shiftPattern(String(v));
  if (k.endsWith('_date') && typeof v === 'string') return dateOnly(v);
  if (k === 'estimated_distance_km') return `${String(v)} km`;
  if (k === 'ambulance_type' && typeof v === 'string') return ambulanceType(v);
  if (typeof v === 'string' && /^[A-Z][A-Z0-9_]+$/.test(v)) return tidyKey(v).charAt(0) + tidyKey(v).slice(1).toLowerCase();
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function factsOf(v: Record<string, unknown>) {
  // A fact the server has no value for is left out, not shown as a row of
  // dashes (2026-10-27: an ambulance job showed "Distance (estimated) —").
  // An ambulance has no type (owner, 2026-10-30): its recorded kind is not shown.
  const entries = Object.entries(v).filter(
    ([key, x]) =>
      key !== 'ambulance_type' && x !== null && x !== undefined && x !== '' && (typeof x !== 'object' || Array.isArray(x)),
  );
  return <Facts items={entries.map(([key, x]) => [KEY_WORDS[key] ?? tidyKey(key), show(key, x)] as [string, ReactNode])} />;
}

/** The service-specific facts. An offer nests them under the service's key
 *  (dispatch_views.go pricingDetail); a won job's `detail` is the flat map
 *  itself (booking_handlers.go itemDetail) — pass `flat` for that. */
export function ServiceFacts({ source, flat }: { source: Record<string, unknown> | null | undefined; flat?: boolean }) {
  if (!source) return null;
  if (flat) return factsOf(source);
  const keys = ['nurse', 'ambulance', 'equipment', 'home_icu', 'physio', 'doctor'];
  for (const k of keys) {
    const v = source[k];
    if (v && typeof v === 'object' && !Array.isArray(v)) return factsOf(v as Record<string, unknown>);
  }
  return null;
}

/* ------------------------------------------------------------------ service tint */

/** The console's per-app tint (theme/tokens.ts APP), keyed by service, so a
 *  nurse job and an ambulance job look the same here as they do at HQ. */
export const SERVICE_TINT: Record<string, { fg: string; bg: string }> = {
  AMBULANCE: { fg: '#11615C', bg: '#E3F1EF' },
  NURSE: { fg: '#A72E39', bg: '#FBE9EB' },
  HOME_ICU: { fg: '#A72E39', bg: '#FBE9EB' },
  PHYSIO: { fg: '#6B3FA0', bg: '#F1EAFB' },
  DOCTOR: { fg: '#2E6FC4', bg: '#E5F0FB' },
  EQUIPMENT: { fg: '#8A4F0E', bg: '#FBF0E2' },
};

export function ServiceDot({ code }: { code: string }) {
  const t = SERVICE_TINT[code] ?? { fg: '#5A6B80', bg: '#EEF1F4' };
  return <span aria-hidden="true" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: t.fg, marginRight: 7 }} />;
}
